import React from 'react';
import type { User, CreateUserRequest } from '../../schemas/validation';

interface UserFormProps {
  editingUser: User | null;
  formData: CreateUserRequest;
  isSubmitting: boolean;
  onFormDataChange: React.Dispatch<React.SetStateAction<CreateUserRequest>>;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({
  editingUser,
  formData,
  isSubmitting,
  onFormDataChange,
  onSubmit,
  onCancel,
}) => {
  return (
    <div className="p-6 border-b border-gray-200 bg-blue-50">
      <form onSubmit={onSubmit} className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {editingUser ? 'Edit User' : 'Create New User'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => onFormDataChange(prev => ({ ...prev, name: e.target.value }))}
              className="input-field w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => onFormDataChange(prev => ({ ...prev, email: e.target.value }))}
              className="input-field w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password {editingUser && '(leave blank to keep current)'}
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={e => onFormDataChange(prev => ({ ...prev, password: e.target.value }))}
              className="input-field w-full"
              required={!editingUser}
            />
          </div>

          <div className="flex flex-col justify-center gap-3">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isAdmin"
                checked={formData.isAdmin}
                onChange={e => onFormDataChange(prev => ({ ...prev, isAdmin: e.target.checked }))}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="isAdmin" className="text-sm font-medium text-gray-700">
                Administrator privileges
              </label>
            </div>
            {!editingUser && (
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="emailVerified"
                  checked={formData.emailVerified}
                  onChange={e =>
                    onFormDataChange(prev => ({ ...prev, emailVerified: e.target.checked }))
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="emailVerified" className="text-sm font-medium text-gray-700">
                  Email verified
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="flex space-x-4">
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
          </button>
          <button type="button" onClick={onCancel} className="btn-secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
